import { createSelector } from 'reselect';
import { isNotEmpty } from 'lib/misc';
import {
  createSystemTransactionIndex,
  createTransactionStateFilter,
  filterSearchIndex,
  getSearchResultEntities,
  getSearchResultMatches,
} from 'lib/search';
import { getSortedEntityList } from 'lib/sort';
import { getSelectorEntityId, getSelectorTransactionState } from 'lib/store';
import { filterTransactionsByIds, getTransactionId } from 'lib/transactions';
import { State } from 'store';
import { SystemTransaction } from 'types/dto';
import { SearchMatchMap } from 'types/search';

const getSystemTransactionState = (state: State) => state.transactions.system;

// Raw store selectors
const getSystemTransactionEntityMap = createSelector(
  getSystemTransactionState,
  entities => entities.entities,
);
const getSystemTransactionIds = createSelector(getSystemTransactionState, entities => entities.ids);
export const getSystemTransactionQuery = createSelector(
  getSystemTransactionState,
  entities => entities.query,
);
export const getSystemTransactionSelected = createSelector(
  getSystemTransactionState,
  entities => entities.selected,
);
export const getSystemTransactionSortDirection = createSelector(
  getSystemTransactionState,
  entities => entities.sortDir ?? undefined,
);
export const getSystemTransactionSortKey = createSelector(
  getSystemTransactionState,
  entities => entities.sortKey ?? undefined,
);
export const getSystemTransactionStatus = createSelector(
  getSystemTransactionState,
  entities => entities.status,
);

// Retrieve a bank transaction entity by its identifier
export const getSystemTransaction = createSelector(
  getSystemTransactionEntityMap,
  getSelectorEntityId,
  (entities, id) => (id ? entities[id] : undefined),
);

// Retrieve all available system transactions enriched with associated system account names
const getSystemTransactions = createSelector(
  getSystemTransactionIds,
  getSystemTransactionEntityMap,
  (ids, transactionMap) => ids.map(id => transactionMap[id]).filter(isNotEmpty),
);

// Build system transaction search index
const getSystemTransactionSearchIndex = createSelector(
  getSystemTransactions,
  createSystemTransactionIndex,
);

// Retrieve system transactions that match the search query
const getSystemTransactionResults = createSelector(
  getSystemTransactions,
  getSelectorTransactionState,
  getSystemTransactionSearchIndex,
  getSystemTransactionQuery,
  (entities, transactionState, searchIndex, searchQuery) =>
    filterSearchIndex(
      searchIndex,
      searchQuery,
      entities,
      createTransactionStateFilter(transactionState),
    ),
);

// Retrieve sorted system transactions that match the search query
export const getSystemTransactionList = createSelector(
  getSystemTransactionResults,
  getSystemTransactionSortDirection,
  getSystemTransactionSortKey,
  (results, sortDir, sortKey) => {
    const entities = getSearchResultEntities(results);
    const sorted = getSortedEntityList(entities, sortKey, sortDir, ['transactionDate']);
    const searchMatches = getSearchResultMatches(results, getTransactionId);

    return [sorted, searchMatches] as [Array<SystemTransaction>, SearchMatchMap<SystemTransaction>];
  },
);

// Get list of currently selected system transactions
export const getSelectedSystemTransactionList = createSelector(
  getSystemTransactions,
  getSystemTransactionSelected,
  filterTransactionsByIds,
);
